import { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, Pause, Play, Save, User as UserIcon, TrendingUp } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api, type AdminUser, type PosMix, type UsageRow } from "../../../api";
import { Eyebrow, Input, Label, Select, Badge } from "../../../components/ui";
import { Button } from "../../../components/ui/Button";
import { fadeUp } from "../../../lib/motion";
import { fmtUsd, fmtRelativeTime, cn } from "../../../lib/format";

const LANGS = [
  { value: "de", label: "German" },
  { value: "es", label: "Spanish" },
  { value: "fr", label: "French" },
  { value: "it", label: "Italian" },
  { value: "pt", label: "Portuguese" },
];
const CEFR = ["A1", "A2", "B1", "B2", "C1", "C2"];

export default function Users() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [cursors, setCursors] = useState<string[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<AdminUser | null>(null);

  async function load(cursor?: string) {
    setBusy(true);
    setErr(null);
    try {
      const res = await api.listUsers(cursor);
      setUsers(res.users);
      setNextCursor(res.next_cursor);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "load failed");
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => { load(); }, []);

  function onNext() {
    if (!nextCursor) return;
    setCursors((c) => [...c, nextCursor]);
    load(nextCursor);
  }
  function onPrev() {
    const trimmed = cursors.slice(0, -1);
    setCursors(trimmed);
    load(trimmed[trimmed.length - 1]);
  }

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return users;
    return users.filter(
      (u) =>
        (u.email || "").toLowerCase().includes(needle) ||
        (u.first_name || "").toLowerCase().includes(needle),
    );
  }, [users, q]);

  async function togglePause(user: AdminUser) {
    try {
      const updated = await api.adminUpdateUser(user.sub, { paused: !user.paused });
      setUsers((prev) => prev.map((u) => (u.sub === user.sub ? { ...u, ...updated } : u)));
      if (selected?.sub === user.sub) setSelected({ ...selected, ...updated });
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "update failed");
    }
  }

  function onSaved(updated: AdminUser) {
    setUsers((prev) => prev.map((u) => (u.sub === updated.sub ? { ...u, ...updated } : u)));
    setSelected(updated);
  }

  return (
    <motion.div initial="hidden" animate="show" variants={fadeUp}>
      <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="font-serif italic text-ink-soft">admin · users</div>
          <h2 className="display text-4xl md:text-5xl mt-2">Learners.</h2>
        </div>
        <div className="relative w-full md:w-72">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…" className="pl-9" />
        </div>
      </div>

      {err && <div className="text-terracotta text-sm mb-4">{err}</div>}

      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[rgba(42,36,26,0.12)] bg-[rgba(42,36,26,0.03)]">
                {["User", "Level", "Joined", "Last active", "Msgs", "Cost", "Status", ""].map((h) => (
                  <th key={h} className="text-left px-4 py-3 eyebrow text-[0.65rem]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr
                  key={u.sub}
                  onClick={() => setSelected(u)}
                  className={cn(
                    "border-b border-[rgba(42,36,26,0.06)] hover:bg-terracotta-tint cursor-pointer transition-colors",
                    selected?.sub === u.sub && "bg-terracotta-tint",
                  )}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-terracotta/15 text-terracotta flex items-center justify-center font-serif text-sm">
                        {(u.first_name || u.email || "?")[0]?.toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium truncate">
                          {u.first_name || <span className="text-ink-faint">unnamed</span>}
                          {u.is_admin && <span className="ml-1 text-terracotta">★</span>}
                        </div>
                        <div className="text-xs text-ink-faint truncate">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-ink-soft">
                    {u.active_language?.toUpperCase()} · {u.cefr_level}
                  </td>
                  <td className="px-4 py-3 text-ink-soft text-xs">
                    {u.activated_at ? new Date(u.activated_at * 1000).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-ink-soft text-xs">{fmtRelativeTime(u.last_bot_msg_at)}</td>
                  <td className="px-4 py-3 font-mono text-xs">
                    {/* Today's quota — this is what the gate actually
                        reads. Lifetime shown beneath as smaller text
                        for context. */}
                    <div title="Today's usage / daily cap">
                      {(u.period_turns_used ?? 0).toLocaleString()}
                      <span className="text-ink-faint">
                        {" / "}
                        {(u.msg_limit ?? 100).toLocaleString()}
                      </span>
                      <span className="text-ink-faint text-[10px] ml-1">today</span>
                    </div>
                    <div className="text-[10px] text-ink-faint mt-0.5" title="Lifetime messages sent">
                      {(u.lifetime_msgs_out ?? 0).toLocaleString()} lifetime
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{fmtUsd(u.lifetime_cost_usd, 4)}</td>
                  <td className="px-4 py-3">
                    {u.paused ? (
                      <Badge tone="warn">Paused</Badge>
                    ) : u.telegram_user_id ? (
                      <Badge tone="good">Active</Badge>
                    ) : (
                      <Badge tone="neutral">Not linked</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={(e) => { e.stopPropagation(); togglePause(u); }}
                      className="p-1.5 rounded hover:bg-terracotta/10 text-ink-soft hover:text-terracotta"
                      title={u.paused ? "Resume" : "Pause"}
                    >
                      {u.paused ? <Play size={14} /> : <Pause size={14} />}
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-10 text-center text-ink-faint">
                    {busy ? "Loading…" : "No users match."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-4 flex gap-2">
        <Button variant="outline" size="sm" onClick={onPrev} disabled={cursors.length === 0 || busy}>Previous</Button>
        <Button variant="outline" size="sm" onClick={onNext} disabled={!nextCursor || busy}>Next →</Button>
      </div>

      <AnimatePresence>
        {selected && (
          <UserDrawer user={selected} onClose={() => setSelected(null)} onSaved={onSaved} onTogglePause={togglePause} />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function UserDrawer({
  user,
  onClose,
  onSaved,
  onTogglePause,
}: {
  user: AdminUser;
  onClose: () => void;
  onSaved: (u: AdminUser) => void;
  onTogglePause: (u: AdminUser) => void;
}) {
  const [detail, setDetail] = useState<{ profile: AdminUser; usage_last_30d: UsageRow[]; recent_messages: unknown[]; pos_mix?: PosMix } | null>(null);
  const [saveBusy, setSaveBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [firstName, setFirstName] = useState(user.first_name || "");
  const [lang, setLang] = useState(user.active_language || "de");
  const [cefr, setCefr] = useState(user.cefr_level || "A1");
  const [target, setTarget] = useState(user.daily_message_target || 12);
  const [msgLimit, setMsgLimit] = useState(user.msg_limit || 100);

  useEffect(() => {
    api.getUserDetail(user.sub).then(setDetail).catch(() => {});
  }, [user.sub]);

  async function save() {
    setSaveBusy(true);
    setErr(null);
    try {
      const updated = await api.adminUpdateUser(user.sub, {
        first_name: firstName,
        active_language: lang,
        cefr_level: cefr,
        daily_message_target: target,
        msg_limit: msgLimit,
      });
      onSaved(updated);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "save failed");
    } finally {
      setSaveBusy(false);
    }
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-ink/40 backdrop-blur-sm z-40"
        onClick={onClose}
      />
      <motion.aside
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        className="fixed top-0 right-0 bottom-0 w-full md:w-[560px] bg-paper z-50 overflow-y-auto shadow-2xl"
      >
        <div className="sticky top-0 bg-paper border-b border-[rgba(42,36,26,0.12)] px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-terracotta/15 text-terracotta flex items-center justify-center font-serif">
              {(user.first_name || user.email)[0]?.toUpperCase()}
            </div>
            <div>
              <div className="font-serif text-xl">{user.first_name || "Unnamed"}</div>
              <div className="text-xs text-ink-faint">{user.email}</div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-terracotta/10 rounded"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-8">
          {/* Quick actions */}
          <div className="flex gap-2 flex-wrap">
            <Button
              size="sm"
              variant={user.paused ? "primary" : "outline"}
              onClick={() => onTogglePause(user)}
            >
              {user.paused ? <><Play size={14} /> Resume</> : <><Pause size={14} /> Pause</>}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                window.location.href = `/app/journey?uid=${encodeURIComponent(user.sub)}`;
              }}
            >
              <TrendingUp size={14} /> Journey
            </Button>
            <Badge tone={user.paused ? "warn" : user.telegram_user_id ? "good" : "neutral"}>
              {user.paused ? "Paused" : user.telegram_user_id ? "Active" : "Not linked"}
            </Badge>
            {user.is_admin && <Badge tone="terracotta">★ Admin</Badge>}
          </div>

          {/* Edit form */}
          <section>
            <Eyebrow>edit profile</Eyebrow>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div>
                <Label>Name</Label>
                <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              </div>
              <div>
                <Label>Language</Label>
                <Select value={lang} onChange={(e) => setLang(e.target.value)}>
                  {LANGS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                </Select>
              </div>
              <div>
                <Label>CEFR level</Label>
                <Select value={cefr} onChange={(e) => setCefr(e.target.value)}>
                  {CEFR.map((c) => <option key={c} value={c}>{c}</option>)}
                </Select>
              </div>
              <div>
                <Label>Daily target</Label>
                <Input type="number" min={0} max={60} value={target} onChange={(e) => setTarget(parseInt(e.target.value || "0", 10))} />
              </div>
              <div>
                <Label>Msg limit</Label>
                <Input type="number" min={0} value={msgLimit} onChange={(e) => setMsgLimit(parseInt(e.target.value || "0", 10))} />
              </div>
            </div>
            <div className="mt-4 flex gap-2 items-center">
              <Button size="sm" onClick={save} disabled={saveBusy}>
                <Save size={14} /> {saveBusy ? "Saving…" : "Save"}
              </Button>
              {err && <span className="text-xs text-terracotta">{err}</span>}
            </div>
          </section>

          {/* POS mix — last 7 days of plan picks vs CEFR target */}
          {detail?.pos_mix && (
            <section>
              <Eyebrow>pos mix · last 7 days</Eyebrow>
              <PosMixChart pos={detail.pos_mix} />
            </section>
          )}

          {/* Usage chart */}
          <section>
            <Eyebrow>last 30 days usage</Eyebrow>
            {detail ? (
              <div className="h-48 mt-3">
                <ResponsiveContainer>
                  <LineChart data={detail.usage_last_30d}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(42,36,26,0.08)" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#8A8079" }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: "#8A8079" }} tickLine={false} axisLine={false} />
                    <Tooltip contentStyle={{ background: "#15181F", border: "none", borderRadius: 6, color: "#F4ECDD", fontSize: 12 }} />
                    <Line type="monotone" dataKey="msgs_out" stroke="#B8472C" strokeWidth={2} dot={false} name="bot" />
                    <Line type="monotone" dataKey="msgs_in" stroke="#17171A" strokeWidth={1.5} dot={false} name="user" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-xs text-ink-faint mt-3">loading…</p>
            )}
          </section>

          {/* Stats grid */}
          <section className="grid grid-cols-2 gap-3">
            <div className="card py-3 px-4">
              <div className="eyebrow">today / daily cap</div>
              <div className="font-serif text-2xl">
                {(user.period_turns_used ?? 0).toLocaleString()}
                <span className="text-ink-faint text-base">
                  {" / "}
                  {(user.msg_limit ?? 100).toLocaleString()}
                </span>
              </div>
              <div className="text-[10px] text-ink-faint mt-0.5">
                resets at {user.daily_reset_at
                  ? new Date(user.daily_reset_at * 1000).toUTCString().slice(17, 22) + " UTC"
                  : "next 00:00 UTC"}
              </div>
            </div>
            <div className="card py-3 px-4">
              <div className="eyebrow">lifetime msgs</div>
              <div className="font-serif text-2xl">{(user.lifetime_msgs_out ?? 0).toLocaleString()}</div>
            </div>
            <div className="card py-3 px-4">
              <div className="eyebrow">lifetime cost</div>
              <div className="font-serif text-2xl">{fmtUsd(user.lifetime_cost_usd, 4)}</div>
            </div>
            <div className="card py-3 px-4">
              <div className="eyebrow">last user msg</div>
              <div className="text-sm">{fmtRelativeTime(user.last_user_msg_at)}</div>
            </div>
            <div className="card py-3 px-4">
              <div className="eyebrow">last bot msg</div>
              <div className="text-sm">{fmtRelativeTime(user.last_bot_msg_at)}</div>
            </div>
          </section>

          {/* Recent messages */}
          {detail && detail.recent_messages.length > 0 && (
            <section>
              <Eyebrow>recent messages</Eyebrow>
              <ul className="mt-3 space-y-2 max-h-72 overflow-y-auto">
                {detail.recent_messages.map((m, i) => {
                  const msg = m as { direction?: string; ts?: string; text?: string };
                  return (
                    <li key={i} className="flex gap-3 items-start text-xs">
                      <UserIcon size={12} className="text-ink-faint shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="text-ink-faint font-mono text-[10px]">
                          {msg.direction} · {msg.ts}
                        </div>
                        <div className="text-ink-soft break-words">{msg.text}</div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}
        </div>
      </motion.aside>
    </>
  );
}

const POS_BUCKETS: Array<{ key: string; label: string }> = [
  { key: "noun", label: "Nouns" },
  { key: "verb", label: "Verbs" },
  { key: "modifier", label: "Modifiers" },
  { key: "function", label: "Function" },
  { key: "idiom", label: "Idioms" },
];

function PosMixChart({ pos }: { pos: PosMix }) {
  // Render a small horizontal bar chart per bucket showing the realized
  // share next to a thin tick at the CEFR target. Skew (realized − target)
  // shown numerically next to each row; warning badge fires when any
  // bucket deviates more than the backend's threshold (15pp).
  if (pos.days_with_data === 0) {
    return (
      <p className="text-xs text-ink-faint mt-3">
        no plan data in the last 7 days yet — this user hasn't been planned
        for, or plans pre-date POS tagging.
      </p>
    );
  }
  return (
    <div className="mt-3 card">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] text-ink-faint">
          {pos.lang.toUpperCase()} · target for {pos.cefr_level}
        </div>
        {pos.warning ? (
          <Badge tone="warn">skew {pos.max_skew_pp.toFixed(0)}pp</Badge>
        ) : (
          <Badge tone="good">on target</Badge>
        )}
      </div>
      <div className="space-y-1.5">
        {POS_BUCKETS.map(({ key, label }) => {
          const r = pos.realized[key] ?? 0;
          const t = pos.target[key] ?? 0;
          const skewPp = (r - t) * 100;
          const realizedPct = r * 100;
          const targetPct = t * 100;
          return (
            <div key={key} className="flex items-center gap-3 text-xs">
              <span className="w-16 shrink-0 text-ink-soft">{label}</span>
              <div className="flex-1 h-3 bg-ink-faint/10 rounded-sm relative overflow-hidden">
                <div
                  className="h-full bg-terracotta/70 rounded-sm"
                  style={{ width: `${Math.max(2, realizedPct)}%` }}
                />
                {/* Target tick */}
                <div
                  className="absolute top-0 bottom-0 w-px bg-ink/60"
                  style={{ left: `${targetPct}%` }}
                  title={`target ${targetPct.toFixed(0)}%`}
                />
              </div>
              <span className="w-20 shrink-0 text-right font-mono">
                {realizedPct.toFixed(0)}%
                <span
                  className={cn(
                    "ml-1 text-[10px]",
                    Math.abs(skewPp) > 15 ? "text-terracotta" : "text-ink-faint",
                  )}
                >
                  {skewPp >= 0 ? "+" : ""}
                  {skewPp.toFixed(0)}
                </span>
              </span>
            </div>
          );
        })}
      </div>
      <div className="mt-2 text-[10px] text-ink-faint">
        bar = realized share over the last {pos.days_with_data} day(s) · tick = CEFR target
      </div>
    </div>
  );
}
